import * as THREE from 'three';

export default class Rock {
    constructor(scene, physics, position = { x: 0, z: 0 }, sizeFactor = 1, isLargeFormation = false) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        this.sizeFactor = sizeFactor; // Controls overall rock size
        this.isLargeFormation = isLargeFormation; // Controls if this is a large rock formation (butte/mesa)
        
        // Rock properties
        this.size = 0; // Final size calculated during creation
        this.mesh = null;
        this.collider = null;
        
        // Create the rock
        this.create();
    }
    
    create() {
        // Base size of the rock
        this.size = this.isLargeFormation 
            ? (8 + Math.random() * 12) * this.sizeFactor // Much larger for formations
            : (0.5 + Math.random() * 2) * this.sizeFactor;
        
        let geometry;
        
        if (this.isLargeFormation) {
            // Create a butte/mesa formation
            const radiusTop = this.size * (0.4 + Math.random() * 0.3); // Tapered top
            const radiusBottom = this.size * (0.7 + Math.random() * 0.3);
            const height = this.size * (1.5 + Math.random() * 0.5);
            const radialSegments = 8; // Low poly look
            const heightSegments = 4;
            
            geometry = new THREE.CylinderGeometry(
                radiusTop, radiusBottom, height,
                radialSegments, heightSegments, false
            );
            
            // Add some random vertex displacement for more natural look
            const vertices = geometry.attributes.position;
            for (let i = 0; i < vertices.count; i++) {
                const x = vertices.getX(i);
                const y = vertices.getY(i);
                const z = vertices.getZ(i);
                
                // Add noise based on height (more at the top)
                const heightFactor = (y + height/2) / height;
                const noise = (Math.random() - 0.5) * this.size * 0.2 * heightFactor;
                
                vertices.setXYZ(i,
                    x + (Math.random() - 0.5) * this.size * 0.1,
                    y,
                    z + (Math.random() - 0.5) * this.size * 0.1
                );
            }
            vertices.needsUpdate = true;
        } else {
            // Regular rock geometry
            const shapeType = Math.floor(Math.random() * 4);
            
            switch (shapeType) {
                case 0:
                    geometry = new THREE.DodecahedronGeometry(this.size, 0);
                    break;
                case 1:
                    geometry = new THREE.OctahedronGeometry(this.size, 0);
                    break;
                case 2:
                    geometry = new THREE.TetrahedronGeometry(this.size, 0);
                    break;
                case 3:
                    geometry = new THREE.IcosahedronGeometry(this.size, 0);
                    break;
            }
        }
        
        // Choose a color based on formation type
        let shade, rockColor;
        if (this.isLargeFormation) {
            // Warmer, redder colors for desert formations
            shade = 0.4 + Math.random() * 0.2;
            rockColor = new THREE.Color(shade + 0.1, shade, shade - 0.1);
            
            // Add orange/red hue variation
            const hueShift = 0.05 + Math.random() * 0.1;
            rockColor.offsetHSL(hueShift, 0.1, 0);
        } else {
            // Regular rock coloring
            shade = 0.3 + Math.random() * 0.3;
            rockColor = new THREE.Color(shade, shade, shade);
            
            // Add a slight random hue for variation
            if (Math.random() > 0.7) {
                const hueShift = 0.05 + Math.random() * 0.05;
                const direction = Math.random() > 0.5 ? 1 : -1;
                rockColor.offsetHSL(hueShift * direction, 0, 0);
            }
        }
        
        const rockMaterial = new THREE.MeshStandardMaterial({
            color: rockColor,
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });
        
        this.mesh = new THREE.Mesh(geometry, rockMaterial);
        
        // Position with appropriate ground embedding
        const groundOffset = this.isLargeFormation ? this.size * 0.1 : this.size * 0.3;
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.mesh.position.set(
            this.position.x,
            terrainY + this.size * 0.5 - groundOffset,
            this.position.z
        );
        
        // Rotation (less random for large formations)
        if (this.isLargeFormation) {
            this.mesh.rotation.y = Math.random() * Math.PI * 2; // Only rotate around Y axis
        } else {
            this.mesh.rotation.x = Math.random() * Math.PI;
            this.mesh.rotation.y = Math.random() * Math.PI;
            this.mesh.rotation.z = Math.random() * Math.PI;
        }
        
        // Scale adjustments
        let scaleX, scaleY, scaleZ;
        if (this.isLargeFormation) {
            // More subtle variations for large formations
            scaleX = 0.9 + Math.random() * 0.2;
            scaleY = 1.0; // Keep height consistent
            scaleZ = 0.9 + Math.random() * 0.2;
        } else {
            // Random non-uniform scale for regular rocks
            scaleX = 0.8 + Math.random() * 0.4;
            scaleY = 0.8 + Math.random() * 0.4;
            scaleZ = 0.8 + Math.random() * 0.4;
        }
        this.mesh.scale.set(scaleX, scaleY, scaleZ);
        
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        
        this.scene.add(this.mesh);
        
        // Add physics collider for the rock if it's large enough
        if (this.size > 0.8) {
            this.createPhysicsCollider(scaleX, scaleY, scaleZ, groundOffset);
        }
    }
    
    createPhysicsCollider(scaleX, scaleY, scaleZ, groundOffset) {
        if (!this.physics || !this.physics.isInitialized) return;
        
        // Create a box collider that approximates the rock shape
        const effectiveSize = this.size * Math.max(scaleX, scaleY, scaleZ);
        const colliderHalfExtents = new THREE.Vector3(
            effectiveSize * 0.8, // Slightly smaller than visual size
            effectiveSize * 0.8,
            effectiveSize * 0.8
        );
        
        const rockHeightMap = this.scene?.userData?.terrainHeightMap;
        const rockTerrainY = rockHeightMap ? rockHeightMap.getHeight(this.position.x, this.position.z) : 0;
        const colliderPosition = new THREE.Vector3(
            this.position.x,
            rockTerrainY + this.size * 0.5 - groundOffset + effectiveSize * 0.1,
            this.position.z
        );
        
        // Create static box collider
        this.collider = this.physics.createStaticBox(
            colliderPosition,
            colliderHalfExtents,
            0.6 // Moderate friction for rocks
        );
        
        // Store reference to mesh for collision visualization if needed
        this.collider.visualMesh = this.mesh;
        
        // Register rock as an obstacle
        this.registerAsObstacle(effectiveSize);
    }
    
    registerAsObstacle(effectiveSize) {
        // Add rock to obstacles list if game has one
        if (this.scene.userData && this.scene.userData.obstacles) {
            this.scene.userData.obstacles.push({
                position: { 
                    x: this.position.x, 
                    y: this.mesh.position.y, 
                    z: this.position.z 
                },
                radius: effectiveSize * 1.2 // Slightly larger than visual size
            });
        }
    }
    
    // For removing the rock from the scene if needed
    remove() {
        // Remove mesh from scene
        if (this.mesh) {
            this.scene.remove(this.mesh);
        }
        
        // Remove from physics world if collider exists
        if (this.physics && this.physics.bodies && this.collider) {
            const index = this.physics.bodies.indexOf(this.collider);
            if (index !== -1) {
                this.physics.bodies.splice(index, 1);
            }
            
            const colliderIndex = this.physics.colliders.indexOf(this.collider);
            if (colliderIndex !== -1) {
                this.physics.colliders.splice(colliderIndex, 1);
            }
        }
    }
}